import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, Check, X } from "lucide-react";

/** One scheduled cart job with an inline "complete" form (serial, photo, optional charge). */
export function CartJobRow({ job, onDone }) {
    const [open, setOpen] = useState(false);
    const [f, setF] = useState({ serial: "", charge_amount: "", charge_description: "", returned_condition: "in_stock" });
    const [photo, setPhoto] = useState(null);
    const [busy, setBusy] = useState(false);

    const complete = async () => {
        setBusy(true);
        try {
            let photo_url = null;
            if (photo) {
                const fd = new FormData();
                fd.append("file", photo);
                const { data } = await api.post("/uploads?kind=cart", fd, { headers: { "Content-Type": "multipart/form-data" } });
                photo_url = data.url || data.download_url || (data.id ? `/api/uploads/${data.id}` : null);
            }
            const { data } = await api.post(`/cart-jobs/${job.id}/complete`, { ...f, photo_url });
            toast.success(data.charge ? `Done. $${data.charge.amount} added to next invoice` : "Job completed");
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    };
    const cancel = async () => { await api.post(`/cart-jobs/${job.id}/cancel`); onDone(); };

    return (
        <div className="p-3 text-sm" data-testid={`cartjob-row-${job.id}`}>
            <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-secondary">{job.type}</span>
                <span className="font-medium">{job.customer_name}</span>
                <span className="text-xs text-muted-foreground truncate flex-1">{job.address} · {job.scheduled_date}</span>
                <Button size="sm" variant="outline" onClick={() => setOpen((v) => !v)} data-testid={`cartjob-open-${job.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" />Complete
                </Button>
                <Button size="sm" variant="ghost" onClick={cancel} data-testid={`cartjob-cancel-${job.id}`}><X className="w-3.5 h-3.5" /></Button>
            </div>
            {job.notes && <div className="text-xs text-muted-foreground mt-1">{job.notes}</div>}
            {open && (
                <div className="grid grid-cols-2 gap-2 mt-3">
                    <Input placeholder={job.type === "retrieval" ? "Serial (optional)" : "Serial # delivered"} value={f.serial}
                           onChange={(e) => setF({ ...f, serial: e.target.value })} data-testid={`cartjob-serial-${job.id}`} />
                    <label className="flex items-center gap-2 text-xs border rounded px-2 cursor-pointer">
                        <Camera className="w-4 h-4" />{photo ? photo.name : "Add photo"}
                        <input type="file" accept="image/*" capture="environment" className="hidden"
                               onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid={`cartjob-photo-${job.id}`} />
                    </label>
                    {job.type !== "delivery" && (
                        <select className="text-xs border rounded px-2 h-9 bg-background col-span-2" value={f.returned_condition}
                                onChange={(e) => setF({ ...f, returned_condition: e.target.value })}>
                            <option value="in_stock">Returned cart is fine</option>
                            <option value="damaged">Returned cart is damaged</option>
                            <option value="lost">Cart was missing</option>
                        </select>
                    )}
                    <Input type="number" placeholder="Charge $ (optional)" value={f.charge_amount}
                           onChange={(e) => setF({ ...f, charge_amount: e.target.value })} data-testid={`cartjob-charge-${job.id}`} />
                    <Input placeholder="Charge reason (deposit, damage…)" value={f.charge_description}
                           onChange={(e) => setF({ ...f, charge_description: e.target.value })} />
                    <Button size="sm" className="col-span-2" onClick={complete} disabled={busy} data-testid={`cartjob-submit-${job.id}`}>
                        {busy ? "Saving…" : "Mark complete"}
                    </Button>
                </div>
            )}
        </div>
    );
}
